import { validator } from '@app/constant/FormConstant'
import { formatParamsFilter } from '@app/libs/formatParamsFilter'
import useRemember from '@app/libs/useRemember'
import { Inertia } from '@inertiajs/inertia'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import DateAdapter from '@mui/lab/AdapterMoment'
import DateTimePicker from '@mui/lab/DateTimePicker'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/styles'
import { useState } from 'react'
import { Controller, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const MuiSelectCustom = styled(TextField)({
  '& .MuiSelect-outlined.MuiSelect-outlined': {
    paddingRight: '0',
  },
  '& .MuiOutlinedInput-root': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
})

const FilterFieldComponent = ({ filter, filterForm }) => {
  const { t } = useTranslation()
  const { register, control, setValue } = filterForm
  const checkedIcon = <CheckBoxIcon fontSize="small" />
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />

  switch (filter.type) {
    case 'string': {
      return (
        <Grid item xs={12}>
          <TextField
            {...register(filter.name)}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            label={t(filter.displayName)}
            name={filter.name}
            size="small"
            variant="outlined"
          />
        </Grid>
      )
    }

    case 'number': {
      return (
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={9}>
              <TextField
                {...register(filter.name)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: 0,
                }}
                label={t(filter.displayName)}
                name={filter.name}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
                type="number"
                variant="outlined"
                onChange={(e) => {
                  e.preventDefault()
                  const { value } = e.target
                  if (!value.match(validator.negative)) {
                    setValue(filter.name, value)
                  }
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                control={control}
                name={`${filter.name}_operator`}
                render={({ field: { onChange, value, ref } }) => (
                  <MuiSelectCustom
                    fullWidth
                    select
                    ref={ref}
                    size="small"
                    value={value}
                    variant="outlined"
                    onChange={onChange}
                  >
                    <MenuItem key="less_than" value="less_than">
                      ≤
                    </MenuItem>
                    <MenuItem key="bigger_than" value="bigger_than">
                      ≥
                    </MenuItem>
                  </MuiSelectCustom>
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      )
    }

    case 'dateRange': {
      return (
        <>
          <Grid container item>
            <Box display="flex">
              <Controller
                control={control}
                name={`${filter.name}_start_at`}
                render={({ field: { onChange, value } }) => (
                  <LocalizationProvider dateAdapter={DateAdapter}>
                    <DateTimePicker
                      label={t('TEXT_FIELD.START')}
                      name={`${filter.name}_start_at`}
                      value={value || null}
                      onChange={(value) => onChange(value.format())}
                    />
                  </LocalizationProvider>
                )}
              />
              <Grid item style={{ textAlign: 'center' }} xs={true}>
                <Typography variant="h5">-</Typography>
              </Grid>
              <Controller
                control={control}
                name={`${filter.name}_end_at`}
                render={({ field: { onChange, value } }) => (
                  <LocalizationProvider dateAdapter={DateAdapter}>
                    <DateTimePicker
                      label={t('TEXT_FIELD.END')}
                      name={`${filter.name}_end_at`}
                      size="small"
                      type="datetime-local"
                      value={value || null}
                      variant="outlined"
                      onChange={(value) => onChange(value.format())}
                    />
                  </LocalizationProvider>
                )}
              />
            </Box>
          </Grid>
        </>
      )
    }

    case 'singleSelect': {
      return (
        <Grid item xs={12}>
          <Controller
            control={control}
            name={filter.name}
            render={({ field: { onChange, value } }) => (
              <TextField
                fullWidth
                select
                InputLabelProps={{
                  shrink: true,
                }}
                label={t(filter.displayName)}
                name={filter.name}
                size="small"
                value={value}
                variant="outlined"
                onChange={onChange}
              >
                {filter.options.map((option, index) => (
                  <MenuItem key={index} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          ></Controller>
        </Grid>
      )
    }
    case 'multichip': {
      return (
        <Grid item xs={12}>
          <Autocomplete
            getOptionLabel={(option) => option?.name ?? ''}
            options={filter.options}
            render={(params) => (
              <TextField
                fullWidth
                label={t(filter.displayName)}
                size="small"
                {...params}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
              />
            )}
            size="small"
            value={
              '' !== filterForm.values[filter.name]
                ? filter.options.find((option) => option.id === filterForm.values[filter.name])
                : null
            }
            onChange={(e, value) => filterForm.setValue(filter.name, value?.id ?? '')}
          />
        </Grid>
      )
    }
    case 'multiSelect': {
      const [timerId, setTimerId] = useRemember('timerId', null)
      const {
        field: { onChange, value },
      } = useController({
        name: filter.name,
        control: filterForm.control,
      })

      return (
        <Grid item xs={12}>
          <Autocomplete
            disableCloseOnSelect
            multiple
            getOptionLabel={(option) => option?.name}
            id="checkboxes-tags-demo"
            inputValue={filterForm.getValues('input_search') || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            limitTags={2}
            options={filter.options}
            renderInput={(params) => (
              <TextField
                InputLabelProps={{
                  shrink: true,
                }}
                label={t(filter.displayName)}
                size="small"
                variant="outlined"
                {...params}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} checkedIcon={checkedIcon} icon={icon} style={{ marginRight: 8 }} />
                {option.name}
              </li>
            )}
            size="small"
            value={value}
            onChange={(e, value) => {
              onChange(value)
            }}
            onInputChange={(e, value, reason) => {
              if ('input' === reason) {
                setValue('input_search', value)
                const data = filterForm.getValues()
                const checkFilter = formatParamsFilter(data)
                if (timerId) clearTimeout(timerId)
                const timer = setTimeout(() => {
                  value &&
                    Inertia.visit(route(filter.router, { ...checkFilter }), {
                      only: [filter.only],
                      preserveState: true,
                    })
                }, 2000)
                setTimerId(timer)
              }
            }}
          />
        </Grid>
      )
    }
    case 'voucherAvailabe': {
      return (
        <Grid item xs={12}>
          <TextField
            {...register(filter.name)}
            fullWidth
            select
            InputLabelProps={{
              shrink: true,
            }}
            label={filter.displayName}
            name={filter.name}
            size="small"
            variant="outlined"
          >
            {filter.options.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )
    }

    case 'dateTime': {
      return (
        <>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={9}>
                <Box display="flex">
                  <Controller
                    control={control}
                    name={filter.name}
                    render={({ field: { onChange, value } }) => (
                      <LocalizationProvider dateAdapter={DateAdapter}>
                        <DateTimePicker
                          label={t('TEXT_FIELD.EXPIRED_DATE')}
                          renderInput={(params) => (
                            <TextField
                              fullWidth
                              InputLabelProps={{
                                shrink: true,
                              }}
                              size="small"
                              {...params}
                            />
                          )}
                          value={value || null}
                          onChange={(value) => value && onChange(value.format())}
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Box>
              </Grid>

              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`${filter.name}_operator`}
                  render={({ field: { onChange, value, ref } }) => (
                    <MuiSelectCustom
                      fullWidth
                      select
                      ref={ref}
                      size="small"
                      value={value}
                      variant="outlined"
                      onChange={onChange}
                    >
                      <MenuItem key="less_than" value="less_than">
                        ≤
                      </MenuItem>
                      <MenuItem key="bigger_than" value="bigger_than">
                        ≥
                      </MenuItem>
                    </MuiSelectCustom>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )
    }
    case 'price': {
      return (
        <>
          <Grid item xs={5}>
            <TextField
              {...register(`min_${filter.name}`)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: { min: 0 },
              }}
              label={t('TEXT_FIELD.MIN_PRICE')}
              name={`min_${filter.name}`}
              size="small"
              type="number"
              variant="outlined"
              onChange={(e) => {
                e.preventDefault()
                const { value } = e.target
                if (!value.match(validator.negative)) {
                  setValue(`min_${filter.name}`, value)
                }
              }}
            />
          </Grid>
          <Grid item style={{ textAlign: 'center' }} xs={2}>
            <Typography variant="h5">-</Typography>
          </Grid>
          <Grid item xs={5}>
            <TextField
              {...register(`max_${filter.name}`)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                inputProps: { min: 0 },
              }}
              label={t('TEXT_FIELD.MAX_PRICE')}
              name={`max_${filter.name}`}
              size="small"
              type="number"
              variant="outlined"
              onChange={(e) => {
                e.preventDefault()
                const { value } = e.target
                if (!value.match(validator.negative)) {
                  setValue(`max_${filter.name}`, value)
                }
              }}
            />
          </Grid>
        </>
      )
    }

    default:
      return null
  }
}

export default FilterFieldComponent
